import { makeStyles } from "tss-react/mui";
import { BUTTON_COLORS } from "@miplatform/constants";
import { getFullName, getInitials, getPhoneNumber } from "@miplatform/utils";
import { mdiEmail, mdiPhone, mdiTrashCanOutline } from "@mdi/js";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { MiAvatar } from "../MiAvatar/MiAvatar";
import { MiBadge } from "../MiBadge/MiBadge";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";

export function MiContactInfoCard({
  contact,
  onClick,
  onDelete,
  isDeleteBtnVisible,
  mainColor = "blue",
  showAccountName = false,
  readOnly,
}) {
  const fullName = contact?.resourceName
    ? contact.resourceName
    : getFullName(contact.firstName, contact.lastName);
  const { classes } = useStyles({
    mainColor,
    readOnly,
    isPrimary: contact?.isPrimary,
  });
  const theme = useTheme();

  return (
    <Box
      data-testid="contact-info-card"
      className={classes.infoContainer}
      onClick={onClick}
    >
      <Box className={classes.info} flex={1}>
        <Box data-testid="avatar" position="relative">
          <MiAvatar
            className={classes.avatar}
            sx={{ bgcolor: `${mainColor}.dark` }}
          >
            {getInitials(fullName)}
          </MiAvatar>
        </Box>
        <Box>
          {showAccountName && (
            <Typography
              color="secondary.grey"
              fontWeight="600"
              fontSize={theme.fontSize.small}
            >
              {contact.accountName}
            </Typography>
          )}
          <Typography
            fontWeight={theme.fontWeight.semiBold}
            fontSize={theme.fontSize.medium}
          >
            {fullName}
          </Typography>
          <Typography color="secondary.grey" fontSize={theme.fontSize.small}>
            {contact.label ||
              contact.title ||
              contact.contactTypeName ||
              contact.resourceTitle}
          </Typography>
        </Box>
      </Box>
      <MiBadge text="Primary" className={classes.badge} flex={0.2} />
      <Box className={classes.info} flex={1} justifyContent="center">
        <Box
          className={classes.preferredContact}
          color={`${mainColor}.dark`}
          bgcolor={`${mainColor}.light`}
        >
          <MiIcon
            path={
              contact.preferredContactMethod === "Phone" ? mdiPhone : mdiEmail
            }
            size={0.6}
          />
          <Tooltip
            title={
              contact.preferredContactMethod === "Phone"
                ? getPhoneNumber(contact.phone)
                : contact.email || contact.resourceEmail
            }
            placement="top"
            arrow
          >
            <Typography
              flex={1}
              fontSize={theme.fontSize.small}
              className={classes.truncateText}
            >
              {contact.preferredContactMethod === "Phone"
                ? getPhoneNumber(contact.phone)
                : contact.email || contact.resourceEmail}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
      {isDeleteBtnVisible && (
        <MiButton
          variant="text"
          color={BUTTON_COLORS.RED}
          startIcon={{
            path: mdiTrashCanOutline,
          }}
          onClick={onDelete}
          data-testid="delete-button"
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()(
  (theme, { mainColor, readOnly = false, isPrimary }) => ({
    infoContainer: {
      backgroundColor: theme.palette.common.white,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: "transparent",
      borderRadius: 6,
      padding: "8px 12px",
      display: "flex",
      gap: theme.spacing(2),
      alignItems: "center",
      justifyContent: "space-between",
      cursor: readOnly ? "default" : "pointer",
      "&:hover": {
        borderColor: readOnly
          ? "transparent"
          : `${theme.palette[mainColor]?.dark}`,
      },
    },
    avatar: {
      ...theme.typography.body1,
      color: theme.palette.common.white,
    },
    badge: {
      backgroundColor: theme.palette.primary.main,
      opacity: isPrimary ? 1 : 0,
    },
    info: {
      gap: theme.spacing(1),
      display: "flex",
      alignItems: "center",
    },
    preferredContact: {
      ...theme.typography.body2,
      borderRadius: 4,
      padding: theme.spacing(1.5),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      width: 120,
      fontSize: theme.fontSize.small,
    },
    truncateText: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  })
);
